import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { fetchEmployeeIdentities } from "../api/employeeIdentities";
import { useAuth } from "./AuthContext";
import {
  buildEmployeeIdentityIndex,
  buildPhotoDirectory,
  type EmployeeIdentity,
} from "../utils/people/peopleDirectory";
import { PeopleDirectoryContext, photoLookup, type PeopleDirectory } from "./PeopleDirectoryContext";

// A stable empty reference so a signed-out mount doesn't churn the memo.
const NO_EMPLOYEES: EmployeeIdentity[] = [];

export function PeopleDirectoryProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const selfId = (user as any)?.id ?? null;
  const selfPhoto = (user as any)?.profile_photo_url ?? null;

  const [employees, setEmployees] = useState<EmployeeIdentity[]>(NO_EMPLOYEES);
  const [wanted, setWanted] = useState(false);

  // Which signed-in user the fetch was for. Checking this ref on resolve (not
  // an `alive` cleanup flag) survives React 18 StrictMode's mount/cleanup/mount
  // replay — so the response is applied, not dropped — and guarantees a late
  // response from a previous user is never applied under the next one.
  const fetchedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!selfId) {
      fetchedFor.current = null;
      setEmployees(NO_EMPLOYEES);
      setWanted(false);
      return;
    }
    if (!wanted || fetchedFor.current === selfId) return;
    fetchedFor.current = selfId;
    fetchEmployeeIdentities()
      .then((list) => {
        if (fetchedFor.current === selfId) setEmployees(Array.isArray(list) ? list : NO_EMPLOYEES);
      })
      .catch(() => {
        // An empty directory is a working page: every row falls back to
        // initials, which is exactly what it showed before there were photos.
      });
  }, [selfId, wanted]);

  // Asked for by the first avatar that has no photo of its own to draw, so a
  // screen whose rows all carry their own photo costs no extra request.
  const requestEmployees = useCallback(() => setWanted(true), []);

  const photos = useMemo(
    () => buildPhotoDirectory(employees, selfId ? { photoUrl: selfPhoto, id: selfId } : null),
    [employees, selfId, selfPhoto],
  );
  const employeeIndex = useMemo(() => buildEmployeeIdentityIndex(employees), [employees]);
  const value = useMemo<PeopleDirectory>(
    () => ({ employees: employeeIndex, photoForUser: photoLookup(photos), requestEmployees }),
    [employeeIndex, photos, requestEmployees],
  );

  return <PeopleDirectoryContext.Provider value={value}>{children}</PeopleDirectoryContext.Provider>;
}
